/**
 * Shared types, constants, and utility components for OP Sync.
 */

import { cn } from '@/lib/utils';
import type { OPSyncDashboardStats } from '@/lib/api';

export type AuditDetailTab = 'mismatches' | 'failed' | 'skipped' | 'history';
export type AuditStep = 'configure' | 'select' | 'results';

export const LOOKBACK_OPTIONS = [1, 3, 7, 14] as const;
export type LookbackDays = (typeof LOOKBACK_OPTIONS)[number];

export const EMPTY_ENTITY_STATS: OPSyncDashboardStats = {
  entityType: '',
  totalRecords: 0,
  pending: 0,
  dispatched: 0,
  deadLetter: 0,
  skipped: 0,
  audited: 0,
  totalMismatches: 0,
  notFoundInOP: 0,
  lastSyncedAt: null,
  lastAuditAt: null,
};

/** Consistent short date format for ops: "21 Apr 14:30" */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

export function buildSinceDate(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

/** Compact inline metric: label + value on one line. */
export function Stat({
  label,
  value,
  className,
}: {
  label: string;
  value: number | string;
  className?: string;
}) {
  const isZero = typeof value === 'number' && value === 0;
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          'font-mono text-sm font-semibold',
          isZero ? 'text-muted-foreground' : (className ?? 'text-foreground'),
        )}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
    </div>
  );
}
